import React, { useEffect, useState } from 'react';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import {
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { SLIDE_OVER_BACKDROP_CLASS, SLIDE_OVER_PANEL_TRANSITION_CLASS, SLIDE_OVER_TOP_OFFSET_STYLE } from './slideOverTransition';
import TrackSegmentedControl from './TrackSegmentedControl';
import SearchableSelect from './SearchableSelect';
import DropdownSelect from './DropdownSelect';

// Small labelled on/off switch (e.g. the "Oracle" overlay toggle). Mirrors the
// dropdown control heights so it sits flush in the inline bar.
const ToggleSwitch = ({ checked, onChange, label, hint, size = 'sm' }) => {
  const track = size === 'lg' ? 'h-6 w-11' : 'h-5 w-9';
  const knob = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  const shift = size === 'lg'
    ? (checked ? 'translate-x-5' : 'translate-x-0.5')
    : (checked ? 'translate-x-4' : 'translate-x-0.5');
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      title={hint}
      className="inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-md"
    >
      {label && (
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
          {label}
        </span>
      )}
      <span
        className={`relative inline-flex ${track} flex-shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block ${knob} transform rounded-full bg-white shadow transition-transform ${shift}`}
        />
      </span>
    </button>
  );
};

/**
 * Filter bar for the Analytics section (Rankings, Errors, Payments).
 *
 * - `inline` mode: renders a compact "Filters" trigger that opens a right-side
 *   drawer matching the dashboard's filter panel pattern.
 * - Standalone (default): renders the controls inside an inline card.
 */
export default function AnalyticsFiltersBar({
  showTrack = true,
  forecastType,
  onForecastTypeChange,
  showForecaster = true,
  selectedUser,
  onUserChange,
  participants = [],
  participantsLoading = false,
  blurSensitive = false,
  showHorizon = false,
  selectedHorizon = null,
  onHorizonChange,
  availableHorizons = [],
  horizonsWithData = [],
  selectedYear,
  onYearChange,
  selectedMonthNum,
  onMonthChange,
  monthOptions,
  yearOptions,
  showEnsembleModel = false,
  ensembleModel = null,
  onEnsembleModelChange,
  ensembleModelOptions = [],
  showOracle = false,
  oracleEnabled = false,
  onOracleChange,
  showReference = false,
  referenceEnabled = true,
  onReferenceChange,
  inline = false,
}) {
  const monthDropdownOptions = monthOptions.map((m) => ({
    value: m.value,
    label: m.label.slice(0, 3),
  }));

  const yearDropdownOptions = yearOptions.map((y) => ({
    value: y,
    label: String(y),
  }));

  // The selected forecaster may not have data on every canonical horizon.
  // Options without data are kept visible but disabled (greyed out).
  const dataHorizonSet = new Set(horizonsWithData);
  const horizonDropdownOptions = availableHorizons.map((h) => ({
    value: h,
    label: h === 0 ? 'Intraday' : `D+${h}`,
    disabled: dataHorizonSet.size > 0 && !dataHorizonSet.has(h),
  }));

  const metricLabel = forecastType === 'probabilistic' ? 'MWI' : 'RMSE';
  const horizonLabel = horizonDropdownOptions.find((h) => h.value === selectedHorizon)?.label;
  const monthShort = monthDropdownOptions.find((m) => m.value === selectedMonthNum)?.label;
  const ensembleDropdownOptions = ensembleModelOptions.map((m) => ({
    value: m.value,
    label: m.label,
  }));

  const inlineControls = (
    <>
      {showTrack && (
        <>
          <TrackSegmentedControl
            selectedTrack={forecastType}
            onSelectTrack={onForecastTypeChange}
            showMetric={false}
          />
          <span
            className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"
            title="Metric used to score this track"
          >
            <span className="uppercase tracking-wider">Metric</span>
            <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-900/50 dark:text-brand-300">
              {metricLabel}
            </span>
          </span>
        </>
      )}

      {showForecaster && (
        <div className="min-w-[220px] flex-1 sm:flex-none sm:w-64">
          <SearchableSelect
            value={selectedUser}
            onChange={onUserChange}
            options={participants.map((p) => ({
              value: p.user,
              label: blurSensitive ? (p.user_username || p.user_email) : p.user_email,
              notEvaluated: p.has_stats === false,
            }))}
            placeholder={
              participantsLoading
                ? 'Loading forecasters...'
                : !participants.length
                  ? 'No forecasters available'
                  : 'Search forecaster...'
            }
            disabled={participantsLoading || !participants.length}
            showCount={true}
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        {showOracle && (
          <ToggleSwitch
            checked={oracleEnabled}
            onChange={onOracleChange}
            label="Oracle"
            hint="Overlay the daily best-forecaster error (the achievable envelope)"
          />
        )}
        {showReference && (
          <ToggleSwitch
            checked={referenceEnabled}
            onChange={onReferenceChange}
            label="Ensemble & contracted"
            hint="Rank the Predico ensemble and contracted forecasters into the field"
          />
        )}
        {showEnsembleModel && ensembleDropdownOptions.length > 0 && (
          <DropdownSelect
            value={ensembleModel}
            options={ensembleDropdownOptions}
            onChange={onEnsembleModelChange}
            widthClass="w-40"
          />
        )}
        {showHorizon && availableHorizons.length > 0 && (
          <DropdownSelect
            value={selectedHorizon}
            options={horizonDropdownOptions}
            onChange={onHorizonChange}
            widthClass="w-24"
            glow
          />
        )}
        <DropdownSelect
          value={selectedMonthNum}
          options={monthDropdownOptions}
          onChange={onMonthChange}
          widthClass="w-20"
        />
        <DropdownSelect
          value={selectedYear}
          options={yearDropdownOptions}
          onChange={onYearChange}
          widthClass="w-20"
        />
      </div>
    </>
  );

  if (inline) {
    return (
      <InlineFilterDrawerTrigger
        showTrack={showTrack}
        forecastType={forecastType}
        onForecastTypeChange={onForecastTypeChange}
        showForecaster={showForecaster}
        selectedUser={selectedUser}
        onUserChange={onUserChange}
        participants={participants}
        participantsLoading={participantsLoading}
        blurSensitive={blurSensitive}
        showHorizon={showHorizon}
        selectedHorizon={selectedHorizon}
        onHorizonChange={onHorizonChange}
        availableHorizons={availableHorizons}
        horizonDropdownOptions={horizonDropdownOptions}
        selectedYear={selectedYear}
        onYearChange={onYearChange}
        selectedMonthNum={selectedMonthNum}
        onMonthChange={onMonthChange}
        monthDropdownOptions={monthDropdownOptions}
        yearDropdownOptions={yearDropdownOptions}
        showEnsembleModel={showEnsembleModel}
        ensembleModel={ensembleModel}
        onEnsembleModelChange={onEnsembleModelChange}
        ensembleDropdownOptions={ensembleDropdownOptions}
        showOracle={showOracle}
        oracleEnabled={oracleEnabled}
        onOracleChange={onOracleChange}
        showReference={showReference}
        referenceEnabled={referenceEnabled}
        onReferenceChange={onReferenceChange}
        metricLabel={metricLabel}
        horizonLabel={horizonLabel}
        monthShort={monthShort}
      />
    );
  }

  return (
    <div className="mb-6 bg-surface dark:bg-gray-870 rounded-xl p-3 border border-gray-200/70 dark:border-gray-600/70 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-1">
          Filters
        </span>
        {inlineControls}
      </div>
    </div>
  );
}

function InlineFilterDrawerTrigger({
  showTrack,
  forecastType,
  onForecastTypeChange,
  showForecaster,
  selectedUser,
  onUserChange,
  participants,
  participantsLoading,
  blurSensitive,
  showHorizon,
  selectedHorizon,
  onHorizonChange,
  availableHorizons,
  horizonDropdownOptions,
  selectedYear,
  onYearChange,
  selectedMonthNum,
  onMonthChange,
  monthDropdownOptions,
  yearDropdownOptions,
  showEnsembleModel,
  ensembleModel,
  onEnsembleModelChange,
  ensembleDropdownOptions = [],
  showOracle,
  oracleEnabled,
  onOracleChange,
  showReference,
  referenceEnabled,
  onReferenceChange,
  metricLabel,
  horizonLabel,
  monthShort,
}) {
  const [open, setOpen] = useState(false);

  // Lock body scroll while drawer is open + close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const summaryParts = [];
  if (showTrack) summaryParts.push(metricLabel);
  if (showHorizon && horizonLabel) summaryParts.push(horizonLabel);
  if (monthShort && selectedYear) summaryParts.push(`${monthShort} ${selectedYear}`);
  if (showOracle && oracleEnabled) summaryParts.push('Oracle');
  if (showReference && !referenceEnabled) summaryParts.push('Competitors only');
  const summary = summaryParts.join(' · ');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <AdjustmentsHorizontalIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <span className="uppercase tracking-wider text-[11px] font-semibold text-gray-500 dark:text-gray-400">
          Filters
        </span>
        {summary && (
          <span className="hidden sm:inline text-gray-700 dark:text-gray-200 font-normal">
            {summary}
          </span>
        )}
        <ChevronDownIcon className="h-3 w-3 text-gray-400" />
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} transition className="relative z-50">
        <DialogBackdrop transition className={SLIDE_OVER_BACKDROP_CLASS} />
        <DialogPanel
          as="aside"
          transition
          aria-label="Analytics filters"
          className={`fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white dark:bg-gray-870 shadow-xl flex flex-col ${SLIDE_OVER_PANEL_TRANSITION_CLASS}`}
          style={SLIDE_OVER_TOP_OFFSET_STYLE}
        >
            <header className="flex-shrink-0 border-b border-gray-200/70 dark:border-gray-600/70 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Filters
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                aria-label="Close filters"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {showTrack && (
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Track
                    </label>
                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                      <span className="uppercase tracking-wider">Metric</span>
                      <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-900/50 dark:text-brand-300">
                        {metricLabel}
                      </span>
                    </span>
                  </div>
                  <TrackSegmentedControl
                    selectedTrack={forecastType}
                    onSelectTrack={onForecastTypeChange}
                    showMetric={false}
                  />
                </section>
              )}

              {showForecaster && (
                <section>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Forecaster
                  </label>
                  <SearchableSelect
                    value={selectedUser}
                    onChange={onUserChange}
                    options={participants.map((p) => ({
                      value: p.user,
                      label: blurSensitive ? (p.user_username || p.user_email) : p.user_email,
                      notEvaluated: p.has_stats === false,
                    }))}
                    placeholder={
                      participantsLoading
                        ? 'Loading forecasters...'
                        : !participants.length
                          ? 'No forecasters available'
                          : 'Search forecaster...'
                    }
                    disabled={participantsLoading || !participants.length}
                    showCount={true}
                  />
                </section>
              )}

              {showHorizon && availableHorizons.length > 0 && (
                <section>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Forecast Horizon
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {horizonDropdownOptions.map((opt) => {
                      const active = opt.value === selectedHorizon;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={opt.disabled}
                          title={opt.disabled ? 'No data for this horizon' : undefined}
                          onClick={() => onHorizonChange(opt.value)}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                            opt.disabled
                              ? 'border-gray-200/70 dark:border-gray-700/70 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                              : active
                                ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600 text-brand-700 dark:text-brand-300'
                                : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {showEnsembleModel && ensembleDropdownOptions.length > 0 && (
                <section>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Ensemble strategy
                  </label>
                  <DropdownSelect
                    value={ensembleModel}
                    options={ensembleDropdownOptions}
                    onChange={onEnsembleModelChange}
                    widthClass="w-full"
                    size="lg"
                  />
                </section>
              )}

              {showOracle && (
                <section>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 pr-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Oracle overlay
                      </label>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Show each day's best-forecaster error.
                      </p>
                    </div>
                    <ToggleSwitch
                      checked={oracleEnabled}
                      onChange={onOracleChange}
                      size="lg"
                    />
                  </div>
                </section>
              )}

              {showReference && (
                <section>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 pr-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Ensemble & contracted
                      </label>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Rank reference forecasts into the field.
                      </p>
                    </div>
                    <ToggleSwitch
                      checked={referenceEnabled}
                      onChange={onReferenceChange}
                      size="lg"
                    />
                  </div>
                </section>
              )}

              <section>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Period
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="block text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      Month
                    </span>
                    <DropdownSelect
                      value={selectedMonthNum}
                      options={monthDropdownOptions}
                      onChange={onMonthChange}
                      widthClass="w-full"
                      size="lg"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      Year
                    </span>
                    <DropdownSelect
                      value={selectedYear}
                      options={yearDropdownOptions}
                      onChange={onYearChange}
                      widthClass="w-full"
                      size="lg"
                    />
                  </div>
                </div>
              </section>
            </div>

            <footer className="flex-shrink-0 border-t border-gray-200/70 dark:border-gray-600/70 px-6 py-4">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-full px-4 py-2.5 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 text-sm font-medium transition-colors"
              >
                Done
              </button>
            </footer>
        </DialogPanel>
      </Dialog>
    </>
  );
}
